import { GetWorkflowResponseFixed, ListWorkflowsResponseFixed } from '../pages/Orchestrator/WorkflowTabCanvas';
import { ListWorkflowExecutionsResponseFixed } from '../pages/Orchestrator/WorkflowTabExecutions';
import { fetchWrapRPC } from '../services/api';
import {
  ComputeDashboardExecutorPoolViewRequest,
  ComputeDashboardStatsRequest,
  ComputeDashboardWorkflowsViewRequest,
  DashboardService_ComputeDashboardExecutorPoolView,
  DashboardService_ComputeDashboardStats,
  DashboardService_ComputeDashboardWorkflowsView,
} from './gen/ai/h2o/orchestrator/v1/dashboard_service_pb';
import {
  CreateExecutorPoolRequest,
  DeleteExecutorPoolRequest,
  ExecutorPoolService_CreateExecutorPool,
  ExecutorPoolService_DeleteExecutorPool,
  ExecutorPoolService_GetExecutorPool,
  ExecutorPoolService_ListExecutorPools,
  ExecutorPoolService_UpdateExecutorPool,
  GetExecutorPoolRequest,
  ListExecutorPoolsRequest,
  UpdateExecutorPoolRequest,
} from './gen/ai/h2o/orchestrator/v1/executor_pool_service_pb';
import {
  ExecutorPoolSpecService_ListExecutorPoolSpecs,
  ListExecutorPoolSpecsRequest,
} from './gen/ai/h2o/orchestrator/v1/executor_pool_spec_service_pb';
import {
  GetPreferencesRequest,
  PreferencesService_GetPreferences,
  PreferencesService_UpdatePreferences,
  UpdatePreferencesRequest,
} from './gen/ai/h2o/orchestrator/v1/preferences_service_pb';
import {
  CreateRunnableRequest,
  DeleteRunnableRequest,
  GetRunnableRequest,
  ListRunnablesRequest,
  RunnableService_CreateRunnable,
  RunnableService_DeleteRunnable,
  RunnableService_GetRunnable,
  RunnableService_ListRunnables,
  RunnableService_UpdateRunnable,
  UpdateRunnableRequest,
} from './gen/ai/h2o/orchestrator/v1/runnable_service_pb';
import {
  CancelWorkflowExecutionRequest,
  DeleteWorkflowExecutionRequest,
  ListWorkflowExecutionsRequest,
  WorkflowExecutionService_CancelWorkflowExecution,
  WorkflowExecutionService_DeleteWorkflowExecution,
  WorkflowExecutionService_ListWorkflowExecutions,
} from './gen/ai/h2o/orchestrator/v1/workflow_execution_service_pb';
import {
  CreateWorkflowRequest,
  DeleteWorkflowRequest,
  ExecuteWorkflowRequest,
  GetWorkflowRequest,
  ListWorkflowsRequest,
  UpdateWorkflowRequest,
  WorkflowService_CreateWorkflow,
  WorkflowService_DeleteWorkflow,
  WorkflowService_ExecuteWorkflow,
  WorkflowService_GetWorkflow,
  WorkflowService_ListWorkflows,
  WorkflowService_UpdateWorkflow,
} from './gen/ai/h2o/orchestrator/v1/workflow_service_pb';
import {
  CreateWorkflowTriggerRequest,
  DeleteWorkflowTriggerRequest,
  ListWorkflowTriggersRequest,
  PauseWorkflowTriggerRequest,
  ResumeWorkflowTriggerRequest,
  UpdateWorkflowTriggerRequest,
  WorkflowTriggerService_CreateWorkflowTrigger,
  WorkflowTriggerService_DeleteWorkflowTrigger,
  WorkflowTriggerService_ListWorkflowTriggers,
  WorkflowTriggerService_PauseWorkflowTrigger,
  WorkflowTriggerService_ResumeWorkflowTrigger,
  WorkflowTriggerService_UpdateWorkflowTrigger,
} from './gen/ai/h2o/orchestrator/v1/workflow_trigger_service_pb';
import { RPC } from './gen/runtime';

export class OrchestratorServiceImpl {
  private basePath: string;
  constructor(basePath?: string) {
    this.basePath = basePath || '';
  }

  public setBasePath = (path: string) => {
    this.basePath = path;
  };

  public async getRunnable(req: GetRunnableRequest) {
    return await fetchWrapRPC(RunnableService_GetRunnable, { basePath: this.basePath })({ ...req });
  }

  public async getRunnables(req: ListRunnablesRequest) {
    return await fetchWrapRPC(RunnableService_ListRunnables, { basePath: this.basePath })({ ...req });
  }

  public async createRunnable(req: CreateRunnableRequest) {
    return await fetchWrapRPC(RunnableService_CreateRunnable, { basePath: this.basePath })({ ...req });
  }

  public async editRunnable(req: UpdateRunnableRequest) {
    return await fetchWrapRPC(RunnableService_UpdateRunnable, { basePath: this.basePath })({ ...req });
  }

  public async deleteRunnable(req: DeleteRunnableRequest) {
    return await fetchWrapRPC(RunnableService_DeleteRunnable, { basePath: this.basePath })({ ...req });
  }

  public async getWorkflows(req: ListWorkflowsRequest) {
    // TODO: Replace type once generated ts api is fixed.
    return await fetchWrapRPC(WorkflowService_ListWorkflows as RPC<ListWorkflowsRequest, ListWorkflowsResponseFixed>, {
      basePath: this.basePath,
    })({ ...req });
  }

  public async getWorkflow(req: GetWorkflowRequest) {
    // TODO: Replace type once generated ts api is fixed.
    return await fetchWrapRPC(WorkflowService_GetWorkflow as RPC<GetWorkflowRequest, GetWorkflowResponseFixed>, {
      basePath: this.basePath,
    })({ ...req });
  }

  public async executeWorkflow(req: ExecuteWorkflowRequest) {
    return await fetchWrapRPC(WorkflowService_ExecuteWorkflow, { basePath: this.basePath })({ ...req });
  }

  public async createWorkflow(req: CreateWorkflowRequest) {
    return await fetchWrapRPC(WorkflowService_CreateWorkflow, { basePath: this.basePath })({ ...req });
  }

  public async editWorkflow(req: UpdateWorkflowRequest) {
    return await fetchWrapRPC(WorkflowService_UpdateWorkflow, { basePath: this.basePath })({ ...req });
  }

  public async deleteWorkflow(req: DeleteWorkflowRequest) {
    return await fetchWrapRPC(WorkflowService_DeleteWorkflow, { basePath: this.basePath })({ ...req });
  }

  public async getExecutorPool(req: GetExecutorPoolRequest) {
    return await fetchWrapRPC(ExecutorPoolService_GetExecutorPool, { basePath: this.basePath })({ ...req });
  }

  public async getExecutorPools(req: ListExecutorPoolsRequest) {
    return await fetchWrapRPC(ExecutorPoolService_ListExecutorPools, { basePath: this.basePath })({ ...req });
  }

  public async deleteExecutorPool(req: DeleteExecutorPoolRequest) {
    return await fetchWrapRPC(ExecutorPoolService_DeleteExecutorPool, { basePath: this.basePath })({
      ...req,
    });
  }

  public async createExecutorPool(req: CreateExecutorPoolRequest) {
    return await fetchWrapRPC(ExecutorPoolService_CreateExecutorPool, { basePath: this.basePath })({
      ...req,
    });
  }

  public async editExecutorPool(req: UpdateExecutorPoolRequest) {
    return await fetchWrapRPC(ExecutorPoolService_UpdateExecutorPool, { basePath: this.basePath })({
      ...req,
    });
  }

  public async getExecutorPoolSpecs(req: ListExecutorPoolSpecsRequest) {
    return await fetchWrapRPC(ExecutorPoolSpecService_ListExecutorPoolSpecs, { basePath: this.basePath })({ ...req });
  }

  public async getPreferences(req: GetPreferencesRequest) {
    return await fetchWrapRPC(PreferencesService_GetPreferences, { basePath: this.basePath })({ ...req });
  }

  public async updatePreferences(req: UpdatePreferencesRequest) {
    return await fetchWrapRPC(PreferencesService_UpdatePreferences, { basePath: this.basePath })({ ...req });
  }
  // TODO: Replace with 'ListWorkflowExecutionsResponse' type once generated ts api is fixed.
  public async getWorkflowExecutions(req: ListWorkflowExecutionsRequest) {
    return await fetchWrapRPC(
      WorkflowExecutionService_ListWorkflowExecutions as RPC<
        ListWorkflowExecutionsRequest,
        ListWorkflowExecutionsResponseFixed
      >,
      { basePath: this.basePath }
    )({ ...req });
  }

  public async deleteWorkflowExecution(req: DeleteWorkflowExecutionRequest) {
    return await fetchWrapRPC(WorkflowExecutionService_DeleteWorkflowExecution, { basePath: this.basePath })({
      ...req,
    });
  }

  public async cancelWorkflowExecution(req: CancelWorkflowExecutionRequest) {
    return await fetchWrapRPC(WorkflowExecutionService_CancelWorkflowExecution, { basePath: this.basePath })({
      ...req,
    });
  }

  public async getWorkflowTriggers(req: ListWorkflowTriggersRequest) {
    return await fetchWrapRPC(WorkflowTriggerService_ListWorkflowTriggers, { basePath: this.basePath })({
      ...req,
    });
  }

  public async updateWorkflowTrigger(req: UpdateWorkflowTriggerRequest) {
    return await fetchWrapRPC(WorkflowTriggerService_UpdateWorkflowTrigger, { basePath: this.basePath })({
      ...req,
    });
  }

  public async createWorkflowTrigger(req: CreateWorkflowTriggerRequest) {
    return await fetchWrapRPC(WorkflowTriggerService_CreateWorkflowTrigger, { basePath: this.basePath })({
      ...req,
    });
  }

  public async deleteWorkflowTrigger(req: DeleteWorkflowTriggerRequest) {
    return await fetchWrapRPC(WorkflowTriggerService_DeleteWorkflowTrigger, { basePath: this.basePath })({
      ...req,
    });
  }

  public async pauseWorkflowTrigger(req: PauseWorkflowTriggerRequest) {
    return await fetchWrapRPC(WorkflowTriggerService_PauseWorkflowTrigger, { basePath: this.basePath })({
      ...req,
    });
  }

  public async resumeWorkflowTrigger(req: ResumeWorkflowTriggerRequest) {
    return await fetchWrapRPC(WorkflowTriggerService_ResumeWorkflowTrigger, { basePath: this.basePath })({
      ...req,
    });
  }

  public async getDashboardStats(req: ComputeDashboardStatsRequest) {
    return await fetchWrapRPC(DashboardService_ComputeDashboardStats, { basePath: this.basePath })({
      ...req,
    });
  }

  public async getDashboardExecutorPoolView(req: ComputeDashboardExecutorPoolViewRequest) {
    return await fetchWrapRPC(DashboardService_ComputeDashboardExecutorPoolView, { basePath: this.basePath })({
      ...req,
    });
  }

  public async getWorkflowsViews(req: ComputeDashboardWorkflowsViewRequest) {
    return await fetchWrapRPC(DashboardService_ComputeDashboardWorkflowsView, { basePath: this.basePath })({
      ...req,
    });
  }
}
